import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { AuthLayout } from "@/redesign/layouts";
import { RdButton } from "@/redesign/components";

type CallbackStatus = "loading" | "success" | "error";

/**
 * Linear-grade OAuth callback. Mirrors legacy `AuthCallback.tsx` behavior:
 * reads `success`, `error`, `tiktok_user`, `platform` query params, toasts,
 * redirects to /reviewer-admin or /dashboard after 2s.
 */
export default function AuthCallbackRedesign() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [status, setStatus] = useState<CallbackStatus>("loading");
  const [message, setMessage] = useState("Processing connection…");

  useEffect(() => {
    const handle = async () => {
      try {
        const success = searchParams.get("success");
        const errorParam = searchParams.get("error");
        const errorMessage = searchParams.get("error_message");
        const tiktokUser = searchParams.get("tiktok_user");
        const platformParam = searchParams.get("platform");

        const platformName = platformParam === "instagram" ? "Instagram" : "TikTok";
        const handleName = tiktokUser || "Unknown";

        if (success === "true") {
          setStatus("success");
          setMessage(`Connected ${platformName} account: @${handleName}`);
          toast({
            title: `${platformName} connected`,
            description: `Your ${platformName} account @${handleName} has been linked.`,
          });
          const isReviewer = user?.email === "mdebbagh56@gmail.com";
          const redirect = isReviewer ? "/reviewer-admin" : "/dashboard";
          setTimeout(() => navigate(redirect), 2000);
          return;
        }

        if (errorParam === "true") {
          throw new Error(errorMessage || "OAuth connection failed");
        }

        const code = searchParams.get("code");
        const state = searchParams.get("state");
        if (code || state) {
          throw new Error(
            "Unexpected callback format — OAuth callback should be handled by edge function"
          );
        }

        navigate("/dashboard");
      } catch (err) {
        const msg = err instanceof Error ? err.message : "An unexpected error occurred";
        setStatus("error");
        setMessage(msg);
        toast({ title: "Connection failed", description: msg, variant: "destructive" });
      }
    };

    if (searchParams.toString()) handle();
    else navigate("/dashboard");
  }, [searchParams, user, navigate, toast]);

  const handleRetry = () => {
    const isReviewer = user?.email === "mdebbagh56@gmail.com";
    navigate(isReviewer ? "/reviewer-admin" : "/dashboard");
  };

  return (
    <AuthLayout>
      <div className="flex flex-col items-center gap-5 py-8 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full">
          {status === "loading" && (
            <Loader2 className="h-9 w-9 animate-spin text-[hsl(var(--rd-accent-500))]" />
          )}
          {status === "success" && (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--rd-success)/0.15)] text-[hsl(var(--rd-success))]">
              <CheckCircle2 className="h-6 w-6" />
            </div>
          )}
          {status === "error" && (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--rd-error)/0.15)] text-[hsl(var(--rd-error))]">
              <XCircle className="h-6 w-6" />
            </div>
          )}
        </div>

        <h1 className="text-[22px] font-semibold tracking-tight">
          {status === "loading" && "Connecting your account"}
          {status === "success" && "Account connected"}
          {status === "error" && "Connection failed"}
        </h1>
        <p className="max-w-sm text-[13.5px] text-[hsl(var(--rd-text-2))]">{message}</p>

        {status === "error" && (
          <RdButton onClick={handleRetry} className="w-full">
            Return to dashboard
          </RdButton>
        )}
        {status === "success" && (
          <p className="text-[12px] text-[hsl(var(--rd-text-3))]">Redirecting in a moment…</p>
        )}
      </div>
    </AuthLayout>
  );
}
